import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { 
  Users, 
  Mail, 
  Phone, 
  MessageSquare, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  Send,
  Filter,
  Download,
  UserPlus
} from "lucide-react";
import { BulkGuestManager } from './BulkGuestManager';

interface Guest {
  id: string;
  email?: string;
  phone?: string;
  name?: string;
  hasVoted: boolean;
  invitedAt: string;
  status: 'pending' | 'responded' | 'declined';
  last_reminder?: string;
}

interface GuestManagementProps {
  eventId: string;
  guests: Guest[];
  onGuestsUpdate: () => void;
}

export const GuestManagement = ({ eventId, guests, onGuestsUpdate }: GuestManagementProps) => {
  const [newGuest, setNewGuest] = useState({ name: '', email: '', phone: '' });
  const [reminderMessage, setReminderMessage] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [showBulkManager, setShowBulkManager] = useState(false);
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [selectedGuests, setSelectedGuests] = useState<string[]>([]);
  const { toast } = useToast();

  const sendInvitationToGuest = async (guest: { name: string; email: string }) => {
    try {
      // Fetch event details to send with invitation
      const { data: event, error: eventError } = await supabase
        .from('events')
        .select('*')
        .eq('id', eventId)
        .single();

      if (eventError || !event) {
        throw new Error('Failed to fetch event details');
      }

      // Send invitation email
      const { error: emailError } = await supabase.functions.invoke('send-invitation-email', {
        body: {
          event: {
            id: event.id,
            name: event.name,
            description: event.description,
            host_name: event.host_name,
            event_type: event.event_type,
            rsvp_date: event.rsvp_date,
            rsvp_time: event.rsvp_time,
            rsvp_location: event.rsvp_location,
            deadline: event.deadline,
            shareable_link: event.shareable_link
          },
          guest: {
            name: guest.name,
            email: guest.email
          },
          shareableLink: event.shareable_link
        }
      });

      if (emailError) {
        console.error('Error sending invitation email:', emailError);
        // Don't throw error here, just log it so guest addition isn't blocked
      }
    } catch (error) {
      console.error('Error in sendInvitationToGuest:', error);
      // Don't throw error here, just log it so guest addition isn't blocked
    }
  };

  const addGuest = async () => {
    if (!newGuest.email && !newGuest.phone) {
      toast({
        title: "Error",
        description: "Please provide either email or phone number",
        variant: "destructive"
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('event_guests')
        .insert([{
          event_id: eventId,
          name: newGuest.name || null,
          email: newGuest.email || null,
          phone: newGuest.phone || null,
        }]);

      if (error) throw error;

      // Send invitation email if guest has email
      if (newGuest.email) {
        await sendInvitationToGuest({
          name: newGuest.name || 'Guest',
          email: newGuest.email
        });
      }

      setNewGuest({ name: '', email: '', phone: '' });
      onGuestsUpdate();
      
      toast({
        title: "Guest added",
        description: newGuest.email ? "Guest added and invitation sent" : "Guest added to the event",
      });
    } catch (error) {
      console.error('Error adding guest:', error);
      toast({
        title: "Error",
        description: "Failed to add guest",
        variant: "destructive"
      });
    }
  };

  const removeGuest = async (guestId: string) => {
    try {
      const { error } = await supabase
        .from('event_guests')
        .delete()
        .eq('id', guestId);

      if (error) throw error;

      onGuestsUpdate();
      
      toast({
        title: "Guest removed",
        description: "Guest has been removed from the event",
      });
    } catch (error) {
      console.error('Error removing guest:', error);
      toast({
        title: "Error",
        description: "Failed to remove guest",
        variant: "destructive"
      });
    }
  };

  const sendReminder = async (guestIds: string[]) => {
    try {
      // Call edge function to send reminder emails
      const { error } = await supabase.functions.invoke('send-reminder-email', {
        body: {
          event_id: eventId,
          guest_ids: guestIds,
          message: reminderMessage
        }
      });

      if (error) throw error;

      toast({
        title: "Reminders sent",
        description: `Sent reminders to ${guestIds.length} guests`,
      });
      
      setShowReminderDialog(false);
      setReminderMessage('');
      setSelectedGuests([]);
    } catch (error) {
      console.error('Error sending reminders:', error);
      toast({
        title: "Error",
        description: "Failed to send reminders",
        variant: "destructive"
      });
    }
  };

  const exportGuestList = () => {
    const csvContent = [
      ['Name', 'Email', 'Phone', 'Status', 'Voted', 'Invited At'],
      ...guests.map(guest => [
        guest.name || '',
        guest.email || '',
        guest.phone || '',
        guest.status,
        guest.hasVoted ? 'Yes' : 'No',
        new Date(guest.invitedAt).toLocaleDateString()
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `guest-list-${eventId}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const filteredGuests = guests.filter(guest => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'voted') return guest.hasVoted;
    if (filterStatus === 'pending') return !guest.hasVoted;
    return true;
  });

  const getStatusBadge = (guest: Guest) => {
    if (guest.hasVoted) {
      return <Badge variant="default" className="bg-green-100 text-green-800">Voted</Badge>;
    }
    return <Badge variant="secondary">Pending</Badge>;
  };

  const handleBulkGuestsChange = async (newGuests: Guest[]) => {
    try {
      // Convert to the format expected by the backend
      const guestsToInsert = newGuests.map(guest => ({
        event_id: eventId,
        name: guest.name || null,
        email: guest.email || null,
        phone: guest.phone || null,
      }));

      // Insert all guests at once
      await Promise.all(guestsToInsert.map(guest => 
        supabase.from('event_guests').insert([guest])
      ));

      // Send invitation emails to guests with email addresses
      const guestsWithEmail = newGuests.filter(guest => guest.email);
      if (guestsWithEmail.length > 0) {
        await Promise.all(guestsWithEmail.map(guest => 
          sendInvitationToGuest({
            name: guest.name || 'Guest',
            email: guest.email!
          })
        ));
      }

      onGuestsUpdate();
      setShowBulkManager(false);
      
      toast({
        title: "Guests added",
        description: guestsWithEmail.length > 0 
          ? `${newGuests.length} guests added and ${guestsWithEmail.length} invitation emails sent`
          : `${newGuests.length} guests added to the event`,
      });
    } catch (error) {
      console.error('Error adding bulk guests:', error);
      toast({
        title: "Error",
        description: "Failed to add guests",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5" />
          <h2 className="text-xl font-semibold">Guest Management</h2>
          <Badge variant="outline">{guests.length} guests</Badge>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowBulkManager(true)}
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Bulk Add
          </Button>
          
          <Button 
            variant="outline" 
            size="sm"
            onClick={exportGuestList}
            disabled={guests.length === 0}
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          
          <Dialog open={showReminderDialog} onOpenChange={setShowReminderDialog}>
            <DialogTrigger asChild>
              <Button 
                variant="outline" 
                size="sm"
                disabled={guests.filter(g => !g.hasVoted).length === 0}
              >
                <Send className="w-4 h-4 mr-2" />
                Send Reminders
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Send Reminder</DialogTitle>
                <DialogDescription>
                  Send a reminder to guests who haven't voted yet
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <Textarea
                  placeholder="Optional custom message..."
                  value={reminderMessage}
                  onChange={(e) => setReminderMessage(e.target.value)}
                />
                <Button 
                  onClick={() => sendReminder(guests.filter(g => !g.hasVoted).map(g => g.id))}
                  className="w-full"
                >
                  Send to {guests.filter(g => !g.hasVoted).length} guests
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Bulk Guest Manager Dialog */}
      <Dialog open={showBulkManager} onOpenChange={setShowBulkManager}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bulk Add Guests</DialogTitle>
            <DialogDescription>
              Add multiple guests at once using the bulk manager
            </DialogDescription>
          </DialogHeader>
          <BulkGuestManager
            guests={[]}
            onGuestsChange={handleBulkGuestsChange}
          />
        </DialogContent>
      </Dialog>

      {/* Quick Add Guest */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Add New Guest</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Input
              placeholder="Name"
              value={newGuest.name}
              onChange={(e) => setNewGuest({...newGuest, name: e.target.value})}
            />
            <Input
              type="email"
              placeholder="Email"
              value={newGuest.email}
              onChange={(e) => setNewGuest({...newGuest, email: e.target.value})}
            />
            <Input
              type="tel"
              placeholder="Phone"
              value={newGuest.phone}
              onChange={(e) => setNewGuest({...newGuest, phone: e.target.value})}
            />
            <Button onClick={addGuest}>Add Guest</Button>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4" />
          <span className="text-sm font-medium">Filter:</span>
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="voted">Voted</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Guest List */}
      <div className="space-y-2">
        {filteredGuests.map((guest) => (
          <Card key={guest.id} className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{guest.name || 'Unknown'}</span>
                    {getStatusBadge(guest)}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    {guest.email && (
                      <div className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {guest.email}
                      </div>
                    )}
                    {guest.phone && (
                      <div className="flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {guest.phone}
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Invited: {new Date(guest.invitedAt).toLocaleDateString()}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {!guest.hasVoted && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => sendReminder([guest.id])}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Remind
                  </Button>
                )}
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => removeGuest(guest.id)}
                >
                  Remove
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {filteredGuests.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No guests found with the current filter.</p>
        </div>
      )}
    </div>
  );
};